/**
 * Occasion landing page configs — each one is a relationship+moment combo
 * with bespoke copy, example, FAQ, and a prefill query for the wizard.
 */

export type OccasionId =
  | 'birthday-for-mom'
  | 'birthday-for-dad'
  | 'retirement-tribute'
  | 'anniversary-appreciation'
  | 'letter-to-aging-parent';

export interface OccasionFAQ {
  q: string;
  a: string;
}

export interface OccasionConfig {
  id: OccasionId;
  path: string;
  // SEO
  metaTitle: string;
  metaDescription: string;
  h1: string;
  subhead: string;
  // Content
  eyebrow: string;
  useCase: string;
  example: {
    title: string;
    body: string;
    signoff: string;
  };
  ctaPrimary: string;
  ctaSecondary?: string;
  // Wizard prefill
  prefill: {
    relationship: string;
    template: string;
    animationTheme?: string;
  };
  faqs: OccasionFAQ[];
  // Breadcrumb label
  breadcrumb: string;
}

export const OCCASIONS: OccasionConfig[] = [
  {
    id: 'birthday-for-mom',
    path: '/birthday-for-mom',
    metaTitle: "A Birthday Message for Mom — Say It NOW",
    metaDescription:
      "Create a Living Announcement for your mother's birthday — a beautiful, heartfelt message she'll keep forever. Free to start.",
    h1: "A birthday she'll never forget.",
    subhead:
      "Not another card. A Living Announcement built around the woman who shaped you — your words, her photos, her moment.",
    eyebrow: "For Mom · Birthday",
    useCase:
      "Mothers spend their lives anticipating everyone else. A Living Announcement turns the spotlight back where it belongs — on her, on this day, in your own voice.",
    example: {
      title: "For Mom, on her 67th",
      body:
        "You taught me what soft strength looks like. Every quiet sacrifice you thought I didn't notice — I noticed. I'm here today because you stayed.",
      signoff: "Happy birthday, Mom. Your daughter.",
    },
    ctaPrimary: "Start her birthday message",
    ctaSecondary: "See a sample",
    prefill: {
      relationship: 'mother',
      template: 'birthday',
      animationTheme: 'elegant_floral',
    },
    breadcrumb: 'Birthday for Mom',
    faqs: [
      {
        q: "What is a Living Announcement?",
        a: "A Living Announcement is a beautifully designed digital message — photos, words, and an optional video — shared with someone while they're still here to receive it. Think of it as a tribute that doesn't wait for a funeral.",
      },
      {
        q: "How long should my mother's birthday message be?",
        a: "Most heartfelt birthday messages land between 80 and 200 words. JacKey, our gentle writing companion, can help you find the right length without making it feel scripted.",
      },
      {
        q: "Can I send it to my mom by text or email?",
        a: "Yes. You can share by SMS, email, WhatsApp, or just a private link. Nothing is posted publicly unless you choose to.",
      },
      {
        q: "Do I need to be a great writer?",
        a: "No. JacKey asks you a few questions and helps you say what you already feel — in your own words, not generic ones.",
      },
    ],
  },
  {
    id: 'birthday-for-dad',
    path: '/birthday-for-dad',
    metaTitle: "A Birthday Message for Dad — Say It NOW",
    metaDescription:
      "Create a Living Announcement for your father's birthday — the words men rarely hear, in a format he'll keep. Free to start.",
    h1: "Say the thing men rarely hear.",
    subhead:
      "Dads often get a card and a steak. Give him the words you've been carrying for years — in a Living Announcement built to be re-read.",
    eyebrow: "For Dad · Birthday",
    useCase:
      "If your father is the kind of man who doesn't ask for much, this is for him. A Living Announcement lets you say out loud what gets passed down quietly — pride, gratitude, the man he made you into.",
    example: {
      title: "For Dad, on 70",
      body:
        "You never told me you loved me out loud, but you showed me every morning at 5am. I see it now. I always saw it. Today is for you.",
      signoff: "Your son.",
    },
    ctaPrimary: "Start his birthday message",
    ctaSecondary: "See a sample",
    prefill: {
      relationship: 'father',
      template: 'birthday',
      animationTheme: 'golden_light',
    },
    breadcrumb: 'Birthday for Dad',
    faqs: [
      {
        q: "What goes inside a Living Announcement for Dad?",
        a: "Photos that meant something, the message you've been meaning to write, optionally a video where he can hear your voice. You choose how much to include.",
      },
      {
        q: "What if my relationship with my dad is complicated?",
        a: "That's where Say It NOW often matters most. You can say what's true without performing — JacKey helps you find honest, kind words.",
      },
      {
        q: "Can he reply to it?",
        a: "Yes. Recipients can send a private reply back. You can also share the link with siblings if you'd like.",
      },
      {
        q: "Is it free?",
        a: "Yes — your first 3 Living Announcements are free. No card required.",
      },
    ],
  },
  {
    id: 'retirement-tribute',
    path: '/retirement-tribute',
    metaTitle: "Retirement Tribute Message — A Living Announcement | Say It NOW",
    metaDescription:
      "Build a meaningful retirement tribute — collected memories, photos, and words from people who worked beside them. Beautiful and lasting.",
    h1: "Forty years deserves more than a sheet cake.",
    subhead:
      "A Living Announcement turns a retirement into the moment it should be — a real tribute, gathered from the people whose lives they shaped.",
    eyebrow: "For a colleague · Retirement",
    useCase:
      "Most retirement parties end and the cards go in a drawer. A Living Announcement is a tribute they can re-read on hard days for years — and forward to the family that never saw who they were at work.",
    example: {
      title: "For Linda, after 38 years",
      body:
        "You hired me when nobody else would. You stayed late so I wouldn't have to. The team you built will keep building because of you. Thank you doesn't cover it — but thank you.",
      signoff: "From your team.",
    },
    ctaPrimary: "Start the tribute",
    ctaSecondary: "See a sample",
    prefill: {
      relationship: 'colleague',
      template: 'gratitude',
      animationTheme: 'memory_constellation',
    },
    breadcrumb: 'Retirement Tribute',
    faqs: [
      {
        q: "Can multiple people contribute to one tribute?",
        a: "Yes — you can collect notes from the team and bring them into one Living Announcement, signed from the group.",
      },
      {
        q: "Can I schedule it to publish on their last day?",
        a: "Yes. Scheduling is available so the tribute lands exactly when it should.",
      },
      {
        q: "Can I include a video message?",
        a: "Yes — up to 5 minutes of video on the Legacy plan, perfect for a goodbye from leadership or the whole team.",
      },
      {
        q: "Is it printable as a keepsake?",
        a: "Yes. Living Announcements can be exported as a PDF keepsake to frame or hand over.",
      },
    ],
  },
  {
    id: 'anniversary-appreciation',
    path: '/anniversary-appreciation',
    metaTitle: "Anniversary Message — A Living Announcement for Your Partner | Say It NOW",
    metaDescription:
      "Create an anniversary Living Announcement — your story, your photos, the words you mean. A keepsake your partner will revisit.",
    h1: "An anniversary they'll re-open every year.",
    subhead:
      "Flowers wilt. Restaurants forget. A Living Announcement is the love letter that gets re-read — with the photos that started it all.",
    eyebrow: "For your partner · Anniversary",
    useCase:
      "After enough years together, words get short. A Living Announcement gives you the space to actually say it — and the design to make it feel like the moment deserves.",
    example: {
      title: "For Marcus, ten years in",
      body:
        "Ten years and I still pick you. The version of me that's standing today exists because you stayed when it was hard. I love you out loud, on purpose, today and every day after.",
      signoff: "Yours, always.",
    },
    ctaPrimary: "Start your anniversary message",
    ctaSecondary: "See a sample",
    prefill: {
      relationship: 'partner',
      template: 'love',
      animationTheme: 'blooming_garden',
    },
    breadcrumb: 'Anniversary Appreciation',
    faqs: [
      {
        q: "Can I make this private — just between us?",
        a: "Yes. Private sharing mode generates a link only your partner can open, and nothing is posted publicly.",
      },
      {
        q: "Can I include our wedding photos?",
        a: "Yes — upload any photos you want. The templates are designed to make them the centerpiece.",
      },
      {
        q: "Can I schedule it for our anniversary date?",
        a: "Yes. Schedule it now and forget about it — it will land on the day, automatically.",
      },
      {
        q: "Can we re-use the design next year?",
        a: "Yes. With Legacy you can set it to repeat annually with a fresh photo or update.",
      },
    ],
  },
  {
    id: 'letter-to-aging-parent',
    path: '/letter-to-aging-parent',
    metaTitle: "A Letter to an Aging Parent — Say It NOW",
    metaDescription:
      "Write the letter you've been meaning to write. A Living Announcement gives aging parents your real words, in a format they can hold.",
    h1: "Write it now. Read it together.",
    subhead:
      "When time gets honest, the words get harder to delay. A Living Announcement is the quiet way to say everything — while they can still receive it.",
    eyebrow: "For an aging parent · Letter",
    useCase:
      "If your mother or father is in their final chapter, you do not need to wait for the eulogy. A Living Announcement lets them hold your gratitude in their hands, hear your voice, and know — clearly — what they meant.",
    example: {
      title: "For my mother",
      body:
        "I want you to know now, while you can hear me: every good thing in my life traces back to a sacrifice you made when nobody was watching. I see it. I always have. Thank you for staying.",
      signoff: "Your daughter, with all my love.",
    },
    ctaPrimary: "Begin your letter",
    ctaSecondary: "Read a sample",
    prefill: {
      relationship: 'parent',
      template: 'gratitude',
      animationTheme: 'handwritten_journal',
    },
    breadcrumb: 'Letter to an Aging Parent',
    faqs: [
      {
        q: "Is this only for end-of-life situations?",
        a: "No. Many people use Say It NOW long before — for a parent who's gotten older, slower, quieter. The point is to not wait.",
      },
      {
        q: "What if I'm not sure what to say?",
        a: "JacKey, our gentle writing companion, asks you a few soft questions and helps you find the words. You write — JacKey just helps you start.",
      },
      {
        q: "Can I include a voice or video message?",
        a: "Yes. With Legacy you can add up to 5 minutes of video — your voice, your face, in their hands.",
      },
      {
        q: "Can I print it as a keepsake?",
        a: "Yes. Export as a printable PDF to frame or keep at their bedside.",
      },
    ],
  },
];

export function getOccasion(id: OccasionId): OccasionConfig | undefined {
  return OCCASIONS.find((o) => o.id === id);
}

export function buildOccasionCtaHref(o: OccasionConfig): string {
  const params = new URLSearchParams({
    occasion: o.id,
    relationship: o.prefill.relationship,
    template: o.prefill.template,
    ...(o.prefill.animationTheme ? { theme: o.prefill.animationTheme } : {}),
  });
  return `/create?${params.toString()}`;
}
